/**
 * columnResolver — replica la lógica de resolución de columnas de la DataTable de
 * Publicaciones para poder listar, en la ficha de Data Health, TODOS los campos
 * configurados en COLUMNAS con el MISMO valor que ve el usuario en la grilla.
 *
 * Fuentes de verdad replicadas (no reinventar):
 *  - src/components/DataTable.tsx (build de columnas core/native/tags, filtro enabled, orden)
 *  - src/components/cells/MergedTomaCatalogCell.tsx (merge toma↔catalog: catalog gana si tiene valor)
 *  - src/utils/columnFormatters.ts (formatValue por tipo)
 *
 * Política de merge (idéntica a MergedTomaCatalogCell, catalogAuthoritative=true):
 *  1. catalogField no vacío → catalog gana (manual si está en manually_edited_fields).
 *  2. catalogField vacío → "—" (no fallback a toma; respeta el bucket del filtro).
 */
import type { ColumnConfigDoc, CoreEntry, NativeEntry, TagEntry } from '../../../services/columnConfig';
import { formatValue, type ColumnFormat, type FormatType } from '../../../utils/columnFormatters';
import { formatContenidoValue } from '../../cells/MergedTomaCatalogCell';

export interface ResolvedColumn {
  group: 'core' | 'native' | 'tags';
  key: string;
  label: string;
  /** enabled !== false → visible en la grilla de Publicaciones. */
  visible: boolean;
  order: number;
  /** Campo de la toma que lee esta columna (row[tomaKey]). */
  tomaKey: string;
  /** Campo del catalog mergeado (row[catalogField]) o null si no hay merge. */
  catalogField: string | null;
  /** Valor de la TOMA ya formateado ("—" si vacío). */
  tomaDisplay: string;
  /** Valor del CATALOG ya formateado ("—" si vacío o sin merge). */
  catalogDisplay: string;
  /** Lo que el usuario ve en Publicaciones (merge aplicado). */
  display: string;
  /** El valor mostrado provino de una edición manual (manually_edited_fields). */
  manual: boolean;
}

function isNonEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') {
    const t = v.trim();
    return t !== '' && t.toLowerCase() !== 'null' && t !== '(NULL)';
  }
  return true;
}

function parseManuallyEdited(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function fmtOf(entry: NativeEntry | TagEntry): ColumnFormat {
  return {
    format: (entry.format as FormatType) || 'text',
    decimals: entry.decimals,
    thousandsSeparator: entry.thousandsSeparator,
    currencySymbol: entry.currencySymbol,
  };
}

function displayRaw(raw: unknown, fmt: ColumnFormat | null): string {
  if (fmt) return formatValue(raw, fmt);
  return isNonEmpty(raw) ? String(raw) : '—';
}

/**
 * Resuelve todas las columnas configuradas (core + native + tags) para una fila,
 * ordenadas por `order`. Devuelve label, visibilidad, valor toma/catalog y el
 * valor final que ve el usuario (merge aplicado).
 */
export function resolveColumns(
  config: ColumnConfigDoc | null,
  row: Record<string, unknown>
): ResolvedColumn[] {
  if (!config) return [];
  const manualFields = parseManuallyEdited(row['manually_edited_fields']);
  const out: ResolvedColumn[] = [];

  const push = (
    group: ResolvedColumn['group'],
    key: string,
    entry: CoreEntry,
    tomaKey: string,
    fmt: ColumnFormat | null
  ) => {
    const catalogField = entry.mergeWithCatalog || null;
    const tomaRaw = row[tomaKey];
    const tomaDisplay = displayRaw(tomaRaw, fmt);

    let catalogDisplay = '—';
    let display = tomaDisplay;
    let manual = false;

    if (catalogField) {
      const catRaw = row[catalogField];
      if (isNonEmpty(catRaw)) {
        const v = catalogField === 'contenido' ? formatContenidoValue(catRaw) : String(catRaw);
        catalogDisplay = v;
        display = v;
        manual = manualFields.includes(catalogField);
      } else {
        // catalogAuthoritative: sin fallback a toma.
        catalogDisplay = '—';
        display = '—';
      }
    }

    out.push({
      group,
      key,
      label: entry.displayName || key,
      visible: entry.enabled !== false,
      order: entry.order ?? 9999,
      tomaKey,
      catalogField,
      tomaDisplay,
      catalogDisplay,
      display,
      manual,
    });
  };

  Object.entries(config.core || {}).forEach(([k, e]) => push('core', k, e, k, null));
  Object.entries(config.native || {}).forEach(([k, e]) =>
    push('native', k, e, e.sourceField || k, fmtOf(e))
  );
  Object.entries(config.tags || {}).forEach(([k, e]) => push('tags', k, e, k, fmtOf(e)));

  out.sort((a, b) => a.order - b.order);
  return out;
}
